import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_API_URL;

const _buHeader = (buId) =>
  buId ? { headers: { "X-Business-Unit-Id": buId } } : {};

/**
 * List ASAP PROFILE / SEARCH requests for the current Business Unit.
 *
 * @param {{ page?: number, pageSize?: number }} params
 * @param {{ buId?: number|string }} options
 */
export const listSearches = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/search/searches`, {
    params,
    ..._buHeader(buId),
  });
  return response.data;
};

/**
 * Fetch a single search with its persisted criteria.
 */
export const getSearch = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/search/searches/${searchId}`,
    _buHeader(buId)
  );
  return response.data;
};

/**
 * Create a new search request — multipart with EITHER a file OR a clientNeedId.
 *
 * @param {{ file?: File, clientNeedId?: number|string }} body
 * @param {{ buId?: number|string }} options
 */
export const createSearch = async (body = {}, options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  if (body.file) {
    formData.append("file", body.file);
  } else if (body.clientNeedId !== undefined && body.clientNeedId !== null) {
    formData.append("client_need_id", String(body.clientNeedId));
  }
  const response = await axios.post(`${BASE_URL}/search/searches`, formData, {
    ..._buHeader(buId),
  });
  return response.data;
};

/**
 * Trigger AI extraction of criteria from the previously uploaded RFP.
 */
export const extractCriteria = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/search/searches/${searchId}/extract`,
    null,
    _buHeader(buId)
  );
  return response.data;
};

/**
 * Persist the user-validated criteria. Body shape:
 *   { rfp_title, client, seniority, geo_country, geo_city, summary,
 *     skills: { required: [...], optional: [...] } }
 */
export const validateCriteria = async (searchId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.put(
    `${BASE_URL}/search/searches/${searchId}/criteria`,
    payload,
    _buHeader(buId)
  );
  return response.data;
};

/**
 * List PILOT ClientNeeds eligible for the "Pick from PILOT" wizard tab —
 * BU-scoped recent rows that have a job_description_url.
 */
export const listEligibleClientNeeds = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/search/client-needs`, {
    params,
    ..._buHeader(buId),
  });
  return response.data;
};

/**
 * Trigger the PDL count/loosen + extract pipeline for a validated search.
 * Returns the complete profiles list (sorted by score).
 */
export const runSearch = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/search/searches/${searchId}/run`,
    null,
    _buHeader(buId)
  );
  return response.data;
};

/** Fetch all persisted profiles for a search (sorted by score desc). */
export const getProfiles = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/search/searches/${searchId}/profiles`,
    _buHeader(buId)
  );
  return response.data;
};

/** Run another billable PDL extract at the next offset. Dedupes by pdl_id. */
export const loadMoreProfiles = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/search/searches/${searchId}/load-more`,
    null,
    _buHeader(buId)
  );
  return response.data;
};

/** Create a Consultant in PILOT from a SearchProfile. */
export const addProfileToPilot = async (searchId, profileId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/search/searches/${searchId}/profiles/${profileId}/add-to-pilot`,
    null,
    _buHeader(buId)
  );
  return response.data;
};

/**
 * Clone a completed search into a new SearchRequest with edited criteria.
 * Returns the new search detail; the original is left untouched. The
 * payload shape is identical to validateCriteria — same skills/geo/seniority
 * fields. The frontend should call runSearch on the returned id next.
 */
export const forkSearch = async (searchId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/search/searches/${searchId}/fork`,
    payload,
    _buHeader(buId)
  );
  return response.data;
};

/**
 * Fetch the constrained list of regions for a country. Returns
 * { country, regions: [...] }. Used by the criteria form's region dropdown.
 */
export const listRegions = async (country, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/search/regions`, {
    params: country ? { country } : {},
    ..._buHeader(buId),
  });
  return response.data;
};

/**
 * Monthly count of search requests for the caller's BU. Returns
 * { rangeMonths, buckets: [{ month: "YYYY-MM", count }, ...] }.
 */
export const getSearchStatisticsMonthly = async (params, options) => {
  const { rangeMonths = 6 } = params || {};
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/search/statistics/monthly`, {
    params: { rangeMonths },
    ..._buHeader(buId),
  });
  return response.data;
};

/**
 * Download the search's candidate profiles as an .xlsx file. Returns the
 * raw blob; the caller is responsible for triggering the download.
 */
export const exportSearchProfiles = async (searchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(
    `${BASE_URL}/search/searches/${searchId}/profiles/export`,
    {
      params: { format: "xlsx" },
      responseType: "blob",
      ..._buHeader(buId),
    }
  );
  return response.data;
};
